"use client";
import { IAccountAssets, IBlocks, ITheme, IUserData } from "@/types";
import React from "react";
import { BlockUpdateFunction } from ".";
import Link from "next/link";
import AdminPopover from "../../content/AdminPopover";
import * as tabler from "@tabler/icons-react";
import DorasIcons from "@/lib/DorasIcons";
import { getTextColorForBackground, isUUID, shadow } from "@/lib/utils";
import { Separator } from "@/components/ui/separator";
import BlockToggle from "./tabs/toggle";
import CarouselOptions from "./tabs/carousel/options";
import CarouselCustomization from "./tabs/carousel/customization";
import {
    Carousel,
    CarouselContent,
    CarouselItem,
    CarouselNext,
    CarouselPrevious,
} from "@/components/ui/carousel";
import { Card, CardContent } from "@/components/ui/card";
import Autoplay from "embla-carousel-autoplay";
import BlockMetadata from "./tabs/metadata";
interface Props {
    admin?: boolean;
    user: IUserData;
    block: IBlocks;
    update: BlockUpdateFunction;
    style: any;
    assets: IAccountAssets[];
    theme: ITheme;
    newBlock?: string | null;
    impersonate?: IUserData | null;
}
export default function BlockCarousel({
    admin,
    user,
    block,
    update,
    style,
    assets,
    theme,
    newBlock,
    impersonate,
}: Props) {
    const [isExpanded, setIsExpanded] = React.useState(false);
    const [blockUpdate, setBlockUpdate] = React.useState(block);
    React.useEffect(() => {
        if (newBlock === block.id) {
            handleExpand();
        }
        return () => {
            window.removeEventListener("closeBlock", handleClose);
        };
    }, [newBlock, block.id]);
    React.useEffect(() => {
        window.addEventListener("closeBlock", {
            handleEvent: (event: CustomEvent) => {
                if (event.detail.id != block.id) {
                    if (isExpanded) {
                        setIsExpanded(false);
                    }
                }
            },
        });
    });
    React.useEffect(() => {
        if (block != blockUpdate) {
            setBlockUpdate(block);
        }
    }, [block]);
    const handleClose = () => {
        setIsExpanded(false);
        window.dispatchEvent(new CustomEvent("collapseBlock"));
    };
    function handleUpdate(value: IBlocks) {
        if (value == null) {
            update(blockUpdate.id, value, true);
            return;
        }
        setBlockUpdate({
            ...blockUpdate,
            ...value,
        });
        if (block == value) return;
        update(blockUpdate.id, value);
    }
    const event = new CustomEvent("expandBlock", {
        detail: {
            id: blockUpdate.id,
            title: "Carousel",
            description:
                "A carousel of images that can be swiped left and right to cycle through.",
            titleOptions: (
                <BlockToggle
                    block={blockUpdate}
                    blockUpdate={setBlockUpdate}
                    update={handleUpdate}
                />
            ),
            content: (
                <div className="flex flex-col gap-3 pb-3">
                    <CarouselOptions
                        key={block.id}
                        account={user}
                        block={blockUpdate}
                        blockUpdate={setBlockUpdate}
                        update={handleUpdate}
                        assets={assets}
                        impersonate={impersonate}
                    />
                    {/* <Separator />
                    <CarouselCustomization
                        block={blockUpdate}
                        update={handleUpdate}
                    /> */}
                    <BlockMetadata block={blockUpdate} update={handleUpdate} />
                </div>
            ),
        },
    });
    const handleExpand = () => {
        window.dispatchEvent(
            new CustomEvent("closeBlock", {
                detail: {
                    id: block.id,
                },
            })
        );
        setIsExpanded(true);
        window.dispatchEvent(event);
    };
    React.useEffect(() => {
        if (isExpanded) {
            window.dispatchEvent(event);
        }
    }, [blockUpdate]);
    // let backgroundStyle = {};
    // if (blockUpdate.colour.toggle && blockUpdate.colour.background_block) {
    //     backgroundStyle = {
    //         background: blockUpdate.colour.background_block,
    //     };
    // }
    let plugins = [];
    if (blockUpdate.options.autoplay) {
        plugins.push(Autoplay({ delay: blockUpdate.options.autoplay_delay }));
    }
    return (
        <AdminPopover
            admin={admin}
            className={block.show ? "" : "opacity-30"}
            onClick={handleExpand}
            title={"Carousel"}
            isOpen={isExpanded}
            onClose={handleClose}
            key={block.id}
        >
            <div
                key={block.id}
                className={`flex w-full flex-row flex-wrap gap-2 p-2 ${user.theme.button_rounded} relative`}
                id="Carousel"
            >
                <Carousel
                    className="w-full"
                    opts={{
                        align: "start",
                        loop: true,
                    }}
                    plugins={plugins}
                >
                    <CarouselContent className="">
                        {blockUpdate.options?.images?.map(
                            (image: any, index: number) => {
                                return (
                                    <CarouselItem
                                        key={index}
                                        className="h-auto"
                                    >
                                        <div className="h-96 w-full p-1">
                                            <div className="flex h-full items-center justify-center">
                                                {image.link ? (
                                                    <a
                                                        href={image.link}
                                                        target="_blank"
                                                        className="relative inline-block h-full w-full"
                                                    >
                                                        <div className="relative h-full w-full">
                                                            <img
                                                                src={image.url}
                                                                className={`absolute left-0 top-0 h-full w-full object-contain ${user.theme.button_rounded === "rounded-full" ? "rounded-xl" : user.theme.button_rounded}`}
                                                                alt="custom icons"
                                                                id="block-icon"
                                                            />
                                                        </div>
                                                    </a>
                                                ) : (
                                                    <div className="relative h-full w-full">
                                                        <img
                                                            src={image.url}
                                                            className={`absolute left-0 top-0 h-full w-full object-contain ${user.theme.button_rounded === "rounded-full" ? "rounded-xl" : user.theme.button_rounded}`}
                                                            alt="custom icons"
                                                            id="block-icon"
                                                        />
                                                    </div>
                                                )}
                                                {image.caption && (
                                                    <p
                                                        className={`absolute bottom-0 w-fit max-w-full truncate text-nowrap px-3 ${user.theme.button_rounded}`}
                                                        style={{
                                                            background:
                                                                user.theme
                                                                    .button,
                                                            color: user.theme
                                                                .text_col_button,
                                                        }}
                                                    >
                                                        {image.caption}
                                                    </p>
                                                )}
                                            </div>
                                        </div>
                                    </CarouselItem>
                                );
                            }
                        )}
                    </CarouselContent>
                    <CarouselPrevious
                        className="hover:scale-105"
                        style={{
                            background: user.theme.button,
                            color: user.theme.text_col_button,
                            borderColor: "transparent",
                            border: 0,
                        }}
                    />
                    <CarouselNext
                        className="hover:scale-105"
                        style={{
                            background: user.theme.button,
                            color: user.theme.text_col_button,
                            borderColor: "transparent",
                            border: 0,
                        }}
                    />
                </Carousel>
            </div>
        </AdminPopover>
    );
}
